import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Share2, Bookmark, Zap, Activity, Target, Check, X, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import ReactDOM from 'react-dom';
import { useNoshStore } from '../lib/store';
import { cancelMealScan, getImageDisplayUrl } from '../lib/api';
import { ProgressiveImage } from './ui/progressive-image';
import { CachedImage } from './ui/CachedImage';
import { formatCalories, formatMacronutrient, formatRemainingCalories } from '../lib/utils';

interface NutritionData {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  sodium: number;
  servingSize: string;
  mealName: string;
  ingredients: string[];
  confidence: number;
  ai_suggestions?: string[];
}

interface UserProfile {
  dailyCalorieGoal: number;
  currentIntake: number;
  age: number;
  gender: 'male' | 'female';
  height: number; // cm
  weight: number; // kg
  activityLevel: 'sedentary' | 'lightly_active' | 'moderately_active' | 'very_active';
  goal: 'maintain' | 'lose' | 'gain';
}

interface NutritionAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'analysis' | 'view'; // 'analysis' for new scans, 'view' for saved meals
  mealId?: string | null; // Meal ID for delete functionality
  onDeleteMeal?: (mealId: string) => void; // Delete callback
}

const NutritionAnalysisModal = ({ isOpen, onClose, mode = 'analysis', mealId, onDeleteMeal }: NutritionAnalysisModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false); // Track if meal was auto-saved
  const [nutritionData, setNutritionData] = useState<NutritionData | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mealImage, setMealImage] = useState<string>('');
  const [imageLoading, setImageLoading] = useState(true);
  const [hasRealData, setHasRealData] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [permanentImagePath, setPermanentImagePath] = useState<string | null>(null);
  
  // Intersection observer for mobile animations
  const [macroProgressVisible, setMacroProgressVisible] = useState(false);
  const [microProgressVisible, setMicroProgressVisible] = useState(false);
  const macroRef = useRef<HTMLDivElement>(null);
  const microRef = useRef<HTMLDivElement>(null);
  
  // Add hasAnimated state for performance optimization
  const [hasAnimated, setHasAnimated] = useState(false);
  
  // Determine the actual mode: 'view' for saved meals, 'analysis' for new scans
  const isViewMode = mode === 'view';
  
  // Get addMeal function from store
  const { addMeal } = useNoshStore();

  // Mock user profile data - this should come from user store/database  
  const userProfile = {
    dailyCalorieGoal: 2100,
    currentIntake: 1240
  };

  // Load meal data from sessionStorage or use mock data
  useEffect(() => {
    if (!isOpen) return;

    const loadMealData = async () => {
      try {
        const storedData = sessionStorage.getItem('mealAnalysisData');
        console.log('🔍 Checking sessionStorage for mealAnalysisData:', !!storedData);
        
        if (storedData) {
          const { mealData, imageUrl, imagePath, timestamp } = JSON.parse(storedData);
          console.log('📦 Found stored meal data:', { 
            hasData: !!mealData, 
            timestamp, 
            imageUrl: !!imageUrl,
            imagePath: !!imagePath,
            mealDataKeys: mealData ? Object.keys(mealData) : 'none'
          });
          
          // Check if data is not too old (max 1 hour)
          const dataAge = Date.now() - new Date(timestamp).getTime();
          const maxAge = 60 * 60 * 1000; // 1 hour
          
          console.log('⏰ Data age check:', { 
            dataAge: Math.round(dataAge / 1000) + 's', 
            maxAge: Math.round(maxAge / 1000) + 's',
            isValid: dataAge < maxAge 
          });
          
          if (dataAge < maxAge && mealData) {
            // Convert AI response to our interface format
            const convertedData: NutritionData = {
              calories: mealData.estimated_calories || mealData.calories || 385,
              protein: mealData.macros?.protein || mealData.protein || 28,
              carbs: mealData.macros?.carbs || mealData.carbs || 18,
              fat: mealData.macros?.fat || mealData.fat || 24,
              fiber: mealData.nutrition?.fiber || mealData.fiber || 12,
              sugar: mealData.nutrition?.sugar || mealData.sugar || 8,
              sodium: mealData.nutrition?.sodium || mealData.sodium || 420,
              servingSize: mealData.serving_size || "1 bowl (280g)",
              mealName: mealData.meal_name || "AI Analyzed Meal",
              ingredients: mealData.ingredients || ["Various ingredients"],
              confidence: mealData.confidence || 85,
              ai_suggestions: mealData.ai_suggestions || []
            };
            
            setNutritionData(convertedData);
            
            // Handle image loading properly - generate signed URL if needed
            await loadMealImage(imageUrl, imagePath);
            
            // Store the permanent storage path for cleanup if needed
            if (imagePath) {
              setPermanentImagePath(imagePath);
              console.log('📂 Stored permanent image path for potential cleanup:', imagePath);
            }
            
            setHasRealData(true);
            console.log('✅ Loaded real meal analysis data:', convertedData);
            console.log('🔍 Original mealData structure:', mealData);
            console.log('🔄 Setting isLoading to false...');
            setIsLoading(false); // ✅ Mark loading as complete
            console.log('✅ Loading state updated to false');
            
            // 🚀 AUTO-SAVE: Automatically save meal for analysis mode only
            if (!isViewMode) {
              await autoSaveMeal(convertedData);
              
              setTimeout(() => {
                sessionStorage.removeItem('mealAnalysisData');
                console.log('🗑️ Cleared sessionStorage data after successful loading');
              }, 1000);
            }
          } else {
            console.log('⚠️ Meal data is too old or invalid, using mock data');
            setNutritionData(mockNutritionData);
            setMealImage('/food.jpg');
            setImageLoading(false);
            setIsLoading(false); // ✅ Mark loading as complete
            // Clean up old session data
            sessionStorage.removeItem('mealAnalysisData');
          }
        } else {
          console.log('ℹ️ No meal data found, using mock data');
          setNutritionData(mockNutritionData);
          setMealImage('/food.jpg');
          setImageLoading(false);
          setIsLoading(false); // ✅ Mark loading as complete
        }
      } catch (error) {
        console.error('❌ Error loading meal data:', error);
        setNutritionData(mockNutritionData);
        setMealImage('/food.jpg');
        setImageLoading(false);
        setIsLoading(false); // ✅ Mark loading as complete
        // Clean up corrupted session data
        sessionStorage.removeItem('mealAnalysisData');
      }
    };

    loadMealData();
  }, [isOpen, isViewMode]);

  // Helper function to load meal image with proper signed URL generation
  const loadMealImage = async (imageUrl: string, imagePath?: string) => {
    try {
      setImageLoading(true);
      
      // Check if the imageUrl is already a valid signed URL that's not expired
      const isSignedUrl = imageUrl && imageUrl.includes('supabase') && imageUrl.includes('token=');
      
      if (isSignedUrl) {
        // Extract expiration from URL to check if it's still valid
        const urlParams = new URL(imageUrl).searchParams;
        const expiresAt = urlParams.get('expires');
        const isExpired = expiresAt ? new Date(parseInt(expiresAt) * 1000) < new Date() : false;
        
        if (!isExpired) {
          console.log('🔗 Reusing existing valid signed URL (no duplicate generation)');
          setMealImage(imageUrl);
          return;
        } else {
          console.log('⏰ Existing signed URL expired, generating fresh one');
        }
      }
      
      // If this looks like a storage path (not a signed URL), generate a fresh signed URL
      if (imageUrl && !isSignedUrl) {
        console.log('🔗 Generating fresh signed URL for storage path:', imageUrl);
        const displayUrl = await getImageDisplayUrl(imageUrl, 86400); // 24 hours
        setMealImage(displayUrl);
        console.log('✅ Generated fresh signed URL for modal image');
      } else {
        // It's already a signed URL but might be expired, generate new one
        console.log('🔄 Regenerating signed URL for expired/invalid URL');
        const pathFromUrl = imageUrl.split('/object/sign/images/')[1]?.split('?')[0];
        if (pathFromUrl) {
          const displayUrl = await getImageDisplayUrl(pathFromUrl, 86400);
          setMealImage(displayUrl);
          console.log('✅ Regenerated fresh signed URL for modal image');
        } else {
          // Fallback to provided URL
          setMealImage(imageUrl);
          console.log('🔗 Using provided image URL as fallback');
        }
      }
    } catch (error) {
      console.error('❌ Failed to load meal image:', error);
      setMealImage('/food.jpg'); // Fallback
    } finally {
      setImageLoading(false);
    }
  };

  // Mock data fallback
  const mockNutritionData: NutritionData = {
    calories: 385,
    protein: 28,
    carbs: 18,
    fat: 24,
    fiber: 12,
    sugar: 8,
    sodium: 420,
    servingSize: "1 bowl (280g)",
    mealName: "Grilled Chicken & Avocado Salad Bowl",
    ingredients: ["Grilled chicken breast", "Fresh avocado", "Cherry tomatoes", "Mixed greens", "Cucumber", "Lettuce", "Olive oil", "Lemon juice"],
    confidence: 94
  };

  // Calculate daily intake progress - now using the loaded data
  const newIntake = userProfile.currentIntake + (nutritionData?.calories || 0);
  const intakePercentage = Math.min((newIntake / userProfile.dailyCalorieGoal) * 100, 100);
  const remainingCalories = userProfile.dailyCalorieGoal - newIntake;

  // 🚀 AUTO-SAVE: Automatically save meal after analysis completes (analysis mode only)
  const autoSaveMeal = async (nutritionData: NutritionData) => {
    if (isSaved || isSaving || isViewMode) return; // Prevent duplicate saves and skip for view mode

    try {
      setIsSaving(true);
      console.log('🚀 [Modal] Auto-saving meal to database...');

      // Use permanent storage path for database, not temporary signed URL
      const imageUrlForDatabase = permanentImagePath || mealImage;
      
      console.log('💾 [Modal] Auto-saving meal with image reference:', {
        permanentPath: !!permanentImagePath,
        imageRef: permanentImagePath || 'using temporary URL',
        tempUrl: mealImage.substring(0, 100) + '...'
      });
      
      // Save meal data to database
      await addMeal({
        mealName: nutritionData.mealName,
        imageUrl: imageUrlForDatabase, // Use permanent storage path
        servingSize: nutritionData.servingSize,
        confidence: nutritionData.confidence,
        calories: nutritionData.calories,
        protein: nutritionData.protein,
        carbs: nutritionData.carbs,
        fat: nutritionData.fat,
        fiber: nutritionData.fiber,
        sugar: nutritionData.sugar,
        sodium: nutritionData.sodium,
        ingredients: nutritionData.ingredients,
        aiSuggestions: nutritionData.ai_suggestions || []
      });
      
      setIsSaved(true);
      console.log('✅ [Modal] Meal auto-saved successfully and logged to nutrition tracking');
      
      // Clear the permanent path since meal is now saved (prevent cleanup)
      setPermanentImagePath(null);
    } catch (error) {
      console.error('❌ [Modal] Failed to auto-save meal:', error);
      // Don't show error to user for auto-save, just log it
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMeal = async () => {
    setIsSaving(true);
    
    try {
      if (nutritionData) {
        // Use permanent storage path for database, not temporary signed URL
        const imageUrlForDatabase = permanentImagePath || mealImage;
        
        console.log('💾 Saving meal with image reference:', {
          permanentPath: !!permanentImagePath,
          imageRef: permanentImagePath || 'using temporary URL',
          tempUrl: mealImage.substring(0, 100) + '...'
        });
        
        // Save meal data to database
        await addMeal({
          mealName: nutritionData.mealName,
          imageUrl: imageUrlForDatabase, // Use permanent storage path
          servingSize: nutritionData.servingSize,
          confidence: nutritionData.confidence,
          calories: nutritionData.calories,
          protein: nutritionData.protein,
          carbs: nutritionData.carbs,
          fat: nutritionData.fat,
          fiber: nutritionData.fiber,
          sugar: nutritionData.sugar,
          sodium: nutritionData.sodium,
          ingredients: nutritionData.ingredients,
          aiSuggestions: nutritionData.ai_suggestions || []
        });
        
        console.log('✅ Meal saved successfully to database with permanent image path');
        
        // Clear the permanent path since meal is now saved (prevent cleanup)
        setPermanentImagePath(null);
      }
      
      // Close modal after successful save
      onClose();
    } catch (error) {
      console.error('❌ Failed to save meal:', error);
      // You could add a toast notification here for better UX
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = async () => {
    // For analysis mode, clean up the unsaved image if we have the path
    if (!isViewMode && permanentImagePath) {
      console.log('🧹 Cleaning up unsaved meal image on cancel:', permanentImagePath);
      try {
        await cancelMealScan(permanentImagePath);
      } catch (error) {
        console.error('❌ Failed to cleanup on cancel:', error);
        // Don't block the cancel action even if cleanup fails
      }
    }
    
    onClose();
  };

  // Handle cleanup when modal is closed via X button
  const handleClose = async () => {
    if (isViewMode) {
      // View mode - just close
      onClose();
    } else {
      // Analysis mode - same as cancel
      await handleCancel();
    }
  };

  // Handle meal deletion
  const handleDeleteMeal = async () => {
    if (mealId && onDeleteMeal) {
      onDeleteMeal(mealId);
      setShowDeleteConfirm(false);
      onClose(); // Close modal after deletion
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Only show loading for analysis mode (new scans), not for view mode (saved meals)
      if (isViewMode) {
        setIsLoading(false);
      } else {
        // Simulate loading time for analysis
        const timer = setTimeout(() => setIsLoading(false), 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, isViewMode]);

  // Intersection Observer setup for mobile animation triggers
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -20% 0px', // Trigger when 80% visible
      threshold: 0.8
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === macroRef.current) {
          setMacroProgressVisible(entry.isIntersecting);
        }
        if (entry.target === microRef.current) {
          setMicroProgressVisible(entry.isIntersecting);
        }
      });
    }, observerOptions);

    // Observe elements when they exist
    if (macroRef.current) observer.observe(macroRef.current);
    if (microRef.current) observer.observe(microRef.current);

    return () => observer.disconnect();
  }, [isMobile, isOpen, isLoading]);

  const microData = [
    { label: "Fiber", value: nutritionData?.fiber || 5, unit: "g", icon: "🌾" },
    { label: "Sugar", value: nutritionData?.sugar || 10, unit: "g", icon: "🍯" },
    { label: "Sodium", value: nutritionData?.sodium || 800, unit: "mg", icon: "🧂" }
  ];

  // Handle mobile resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Body scroll lock for mobile bottom sheet (like Add Item modal)
  useEffect(() => {
    if (isOpen && isMobile && isViewMode) {
      // Store original values
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const scrollY = window.scrollY;
      
      // Lock body scrolling completely
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      
      // Prevent touch scrolling on mobile
      document.body.style.touchAction = 'none';
      document.documentElement.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
      
      return () => {
        // Restore everything
        document.body.style.overflow = originalStyle;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = '';
        document.body.style.height = '';
        document.body.style.touchAction = '';
        document.documentElement.style.overflow = '';
        document.body.classList.remove('modal-open');
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, isMobile, isViewMode]);

  // Reset hasAnimated when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasAnimated(false);
    }
  }, [isOpen]);

  // Clean up when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNutritionData(null);
      setIsLoading(true);
      setIsSaving(false);
      setIsSaved(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Modal content component
  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      {/* Desktop backdrop - only show on desktop */}
      {!isMobile && (
        <motion.div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={hasAnimated ? { duration: 0 } : { duration: 0.1, ease: "linear" }}
          onClick={isViewMode ? onClose : handleCancel}
        />
      )}
      
      {/* Container */}
      <div 
        className={`${
          isMobile 
            ? 'h-full' // Mobile: full height
            : 'fixed inset-0 flex items-center justify-center p-4' // Desktop: perfectly centered
        }`}
        onClick={!isMobile ? (isViewMode ? onClose : handleCancel) : undefined}
      >
        <motion.div
          className={`w-full overflow-hidden ${
            isMobile 
              ? 'h-full bg-white dark:bg-gray-800' // Mobile: full screen, no rounded corners
              : 'bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 max-w-4xl mx-4 max-h-[95vh]' // Desktop: 896px width modal
          }`}
          initial={{ 
            opacity: 0, 
            scale: 0.95, 
            y: 20 // Consistent smooth fade-in for both mobile and desktop
          }}
          animate={{ 
            opacity: 1, 
            scale: 1, 
            y: 0 
          }}
          exit={{ 
            opacity: 0, 
            scale: 0.95, 
            y: 20 // Consistent smooth fade-out for both mobile and desktop
          }}
          transition={hasAnimated ? { duration: 0 } : { duration: 0.15, ease: "linear" }}
          onAnimationComplete={() => setHasAnimated(true)}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-between flex-1">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Nutrition Analysis
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {isLoading ? 'Loading analysis...' : 'AI-powered meal analysis'}
                  </p>
                </div>
                {/* Delete Button - Only show in view mode for saved meals */}
                {isViewMode && mealId && onDeleteMeal && (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="rounded-full text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 ml-4"
                  >
                    <Trash2 size={20} />
                  </Button>
                )}
              </div>
            </div>
            {/* X button - Both Mobile and Desktop */}
            <button
              onClick={isViewMode ? onClose : handleCancel}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X size={20} className="text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          {/* Content */}
          <div className={`flex-1 overflow-y-auto ${isMobile ? 'h-full' : 'max-h-[calc(90vh-180px)]'}`}>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-300">Analyzing your meal...</p>
                </div>
              </div>
            ) : nutritionData ? (
              <div className={`${isMobile ? 'space-y-4 p-4 pb-32' : 'grid grid-cols-2 gap-6 p-6'}`}>
                {/* Left Column - Taller Square Image */}
                <div className="space-y-4">
                  {/* Meal Image - Made taller and more square */}
                  <div className="relative rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700" style={{ aspectRatio: isMobile ? '1' : '0.9' }}>
                    {imageLoading ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <CachedImage
                        src={mealImage}
                        alt={nutritionData.mealName}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        fallbackSrc="/food.jpg"
                      />
                    )}
                    
                    {/* Confidence Badge - only show for photo scans (analysis mode), not for saved recipe meals */}
                    {!isViewMode && (
                      <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-lg text-sm font-medium">
                        {nutritionData.confidence}% confident
                      </div>
                    )}
                  </div>

                  {/* Mobile: Show meal info and daily progress below image */}
                  {isMobile && (
                    <>
                      {/* Meal Info */}
                      <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-4">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                          {nutritionData.mealName}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                          Serving size: {nutritionData.servingSize}
                        </p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-300">Total Calories</p>
                            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                              {formatCalories(nutritionData.calories)}
                            </p>
                          </div>
                          <Zap className="text-orange-500" size={28} />
                        </div>
                      </div>

                      {/* Daily Progress */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">Daily Calorie Intake</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatRemainingCalories(userProfile.dailyCalorieGoal - (userProfile.currentIntake + nutritionData.calories))}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-300">Current: {userProfile.currentIntake} cal</span>
                            <span className="text-gray-600 dark:text-gray-300">Goal: {userProfile.dailyCalorieGoal} cal</span>
                          </div>
                          
                          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 relative overflow-hidden">
                            {/* Current intake (blue) - Top layer, grows first */}
                            <motion.div
                              className="bg-blue-400 h-full rounded-full relative z-10"
                              initial={{ width: "0%" }}
                              animate={{ width: `${Math.min((userProfile.currentIntake / userProfile.dailyCalorieGoal) * 100, 100)}%` }}
                              transition={{ duration: 1, ease: "easeOut" }}
                            />
                            {/* Total intake (orange) - Background layer, starts halfway through */}
                            <motion.div
                              className="bg-orange-400 h-full rounded-full absolute top-0 left-0"
                              initial={{ width: "0%" }}
                              animate={{ width: `${Math.min(((userProfile.currentIntake + nutritionData.calories) / userProfile.dailyCalorieGoal) * 100, 100)}%` }}
                              transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                            />
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3 text-xs">
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                <span className="text-gray-600 dark:text-gray-300">Current</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                                <span className="text-gray-600 dark:text-gray-300">+ This meal</span>
                              </div>
                            </div>
                            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                              {Math.round(((userProfile.currentIntake + nutritionData.calories) / userProfile.dailyCalorieGoal) * 100)}% of goal
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Desktop-only: Ingredients below image */}
                  {!isMobile && nutritionData.ingredients.length > 0 && (
                    <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Detected Ingredients</h3>
                      <div className="flex flex-wrap gap-2">
                        {nutritionData.ingredients.map((ingredient, index) => (
                          <span
                            key={index}
                            className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-700"
                          >
                            {ingredient}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Meal Info and Daily Progress Stacked */}
                <div className="space-y-4">
                  {/* Desktop-only: Meal Info at top of right column */}
                  {!isMobile && (
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-3">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                        {nutritionData.mealName}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                        Serving size: {nutritionData.servingSize}
                      </p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-300">Total Calories</p>
                          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                            {formatCalories(nutritionData.calories)}
                          </p>
                        </div>
                        <Zap className="text-orange-500" size={24} />
                      </div>
                    </div>
                  )}

                  {/* Desktop-only: Daily Progress below meal info */}
                  {!isMobile && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-base font-semibold text-gray-700 dark:text-gray-300">Daily Calorie Intake</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatRemainingCalories(userProfile.dailyCalorieGoal - (userProfile.currentIntake + formatCalories(nutritionData.calories)))}
                        </span>
                      </div>
                      
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600 dark:text-gray-300">Current: {userProfile.currentIntake} cal</span>
                          <span className="text-gray-600 dark:text-gray-300">Goal: {userProfile.dailyCalorieGoal} cal</span>
                        </div>
                        
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 relative overflow-hidden">
                          {/* Current intake (blue) - Top layer, grows first */}
                          <motion.div
                            className="bg-blue-400 h-full rounded-full relative z-10"
                            initial={{ width: "0%" }}
                            animate={{ width: `${Math.min((userProfile.currentIntake / userProfile.dailyCalorieGoal) * 100, 100)}%` }}
                            transition={{ duration: 1, ease: "easeOut" }}
                          />
                          {/* Total intake (orange) - Background layer, starts halfway through */}
                          <motion.div
                            className="bg-orange-400 h-full rounded-full absolute top-0 left-0"
                            initial={{ width: "0%" }}
                            animate={{ width: `${Math.min(((userProfile.currentIntake + nutritionData.calories) / userProfile.dailyCalorieGoal) * 100, 100)}%` }}
                            transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                          />
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2 text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                              <span className="text-gray-600 dark:text-gray-300">Current</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                              <span className="text-gray-600 dark:text-gray-300">+ This meal</span>
                            </div>
                          </div>
                          <div className="text-xs font-medium text-gray-800 dark:text-gray-200">
                            {Math.round(((userProfile.currentIntake + nutritionData.calories) / userProfile.dailyCalorieGoal) * 100)}% of goal
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Macros */}
                  <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-3">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Macronutrients</h3>
                    <div className="space-y-2">
                      {[
                        { name: 'Protein', value: formatMacronutrient(nutritionData.protein), unit: 'g', color: 'bg-red-500', target: 80 },
                        { name: 'Carbs', value: formatMacronutrient(nutritionData.carbs), unit: 'g', color: 'bg-yellow-500', target: 100 },
                        { name: 'Fat', value: formatMacronutrient(nutritionData.fat), unit: 'g', color: 'bg-purple-500', target: 50 }
                      ].map((macro) => (
                        <div key={macro.name} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${macro.color}`}></div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{macro.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-16 md:w-32 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                              <motion.div
                                className={`h-1.5 rounded-full ${macro.color}`}
                                initial={{ width: "0%" }}
                                animate={{ width: `${Math.min((macro.value / macro.target) * 100, 100)}%` }}
                                transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                              />
                            </div>
                            <span className="text-xs font-medium text-gray-900 dark:text-white w-10 text-right">
                              {macro.value}{macro.unit}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Other Nutrients */}
                  <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-3">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Other Nutrients</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        { name: 'Fiber', value: formatMacronutrient(nutritionData.fiber), unit: 'g', icon: '🌾' },
                        { name: 'Sugar', value: formatMacronutrient(nutritionData.sugar), unit: 'g', icon: '🍯' },
                        { name: 'Sodium', value: Math.round(nutritionData.sodium), unit: 'mg', icon: '🧂' }
                      ].map((nutrient) => (
                        <div key={nutrient.name} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-600/50 rounded-lg">
                          {/* Left: Emoji - smaller and consistent */}
                          <div className="text-base flex-shrink-0">{nutrient.icon}</div>
                          
                          {/* Right: Value on top, Label below - more compact */}
                          <div className="flex-1 min-w-0 text-center md:text-left">
                            <div className="font-bold text-gray-800 dark:text-white text-sm">
                              {nutrient.value}{nutrient.unit}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-300">{nutrient.name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mobile-only: Ingredients */}
                  {isMobile && nutritionData.ingredients.length > 0 && (
                    <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Detected Ingredients</h3>
                      <div className="flex flex-wrap gap-2">
                        {nutritionData.ingredients.map((ingredient, index) => (
                          <span
                            key={index}
                            className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-700"
                          >
                            {ingredient}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Auto-Save Status - Mobile (Analysis Mode Only) */}
                  {isMobile && !isViewMode && (
                    <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-4">
                      {isSaving ? (
                        <div className="flex items-center gap-3 text-blue-600 dark:text-blue-400">
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          <span className="font-medium">Saving meal to tracker...</span>
                        </div>
                      ) : isSaved ? (
                        <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                          <Check size={20} />
                          <span className="font-medium">✅ Meal saved and logged to nutrition tracking!</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                          <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          <span className="font-medium">Analyzing meal...</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Suggestions */}
                  {nutritionData.ai_suggestions && nutritionData.ai_suggestions.length > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-700 p-4">
                      <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-300 mb-3 flex items-center gap-2">
                        <Target size={20} />
                        AI Insights
                      </h3>
                      <div className="space-y-2">
                        {nutritionData.ai_suggestions.map((suggestion, index) => {
                          // Check if this suggestion is about a recipe
                          const recipeMatch = suggestion.match(/^Cooked from recipe: (.+)$/);
                          
                          if (recipeMatch) {
                            const recipeName = recipeMatch[1];
                            return (
                              <div key={index} className="flex items-start gap-2">
                                <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-purple-800 dark:text-purple-200">
                                  Cooked from recipe: {' '}
                                  <button
                                    onClick={async () => {
                                      // Find the recipe in the store by name
                                      const { useNoshStore } = await import('../lib/store');
                                      const recipes = useNoshStore.getState().recipes;
                                      const matchingRecipe = recipes.find(recipe => 
                                        recipe.title === recipeName || recipe.title.toLowerCase() === recipeName.toLowerCase()
                                      );
                                      
                                      if (matchingRecipe) {
                                        // Close the nutrition modal first
                                        onClose();
                                        
                                        // Open the recipe modal
                                        const { showSavedRecipe } = useNoshStore.getState();
                                        await showSavedRecipe(matchingRecipe);
                                      } else {
                                        console.warn('Recipe not found:', recipeName);
                                      }
                                    }}
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium transition-colors duration-200"
                                  >
                                    {recipeName}
                                  </button>
                                </p>
                              </div>
                            );
                          }
                          
                          // Regular suggestion (not a recipe)
                          return (
                            <div key={index} className="flex items-start gap-2">
                              <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-purple-800 dark:text-purple-200">{suggestion}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-600 dark:text-gray-300">No nutrition data available</p>
              </div>
            )}
          </div>

          {/* Footer Status - Show auto-save status for analysis mode */}
          {!isLoading && nutritionData && !isViewMode && !isMobile && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                {isSaving ? (
                  <div className="flex items-center gap-3 text-blue-600 dark:text-blue-400">
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <span className="font-medium">Saving meal to tracker...</span>
                  </div>
                ) : isSaved ? (
                  <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                    <Check size={20} />
                    <span className="font-medium">✅ Meal saved and logged to nutrition tracking!</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span className="font-medium">Analyzing meal...</span>
                  </div>
                )}
                
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="ml-4"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );

  // Modal JSX with conditional rendering for mobile vs desktop
  const modalJSX = (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={hasAnimated ? { duration: 0 } : { duration: 0.1, ease: "linear" }}
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 9998,
          margin: 0, 
          padding: 0 
        }}
      />

      {/* Modal */}
      {isMobile && isViewMode ? (
        // Mobile View Mode: Gentle fade-in (no scaling or movement)
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={hasAnimated ? { duration: 0 } : { 
            duration: 0.2,
            ease: "easeOut"
          }}
          onAnimationComplete={() => setHasAnimated(true)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'white',
            zIndex: 9999,
            overflow: 'hidden',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
          className="dark:bg-gray-900"
        >
          {/* Mobile bottom sheet content */}
          <div className="relative flex-1 overflow-y-auto">
            {modalContent}
            
            {/* Sticky Close Button for Mobile View Mode */}
            <div className="sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 p-4">
              <Button
                onClick={onClose}
                className="w-full h-12 rounded-3xl text-base font-medium"
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </motion.div>
      ) : (
        // Desktop or Mobile Analysis Mode: Center modal
        <div 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            margin: 0, 
            padding: isMobile ? '0' : '0 1rem'
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: isMobile ? 1 : 0.95, y: isMobile ? 0 : 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: isMobile ? 1 : 0.95, y: isMobile ? 0 : 20 }}
            transition={hasAnimated ? { duration: 0 } : { duration: 0.2, ease: "easeOut" }}
            onAnimationComplete={() => setHasAnimated(true)}
            className="relative w-full max-w-4xl max-h-[95vh] overflow-hidden rounded-2xl bg-white dark:bg-slate-800 shadow-xl border border-gray-200 dark:border-slate-700"
          >
            {modalContent}
          </motion.div>
        </div>
      )}
    </>
  );

  return ReactDOM.createPortal(
    <>
      {modalJSX}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowDeleteConfirm(false)} />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden relative z-10"
          >
            {/* Modal Content */}
            <div className="text-center p-6 pb-4">
              <div className="mb-4">
                <Trash2 size={48} className="text-red-600 dark:text-red-400 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Delete Meal
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                Are you sure you want to delete this meal from your tracker? This action cannot be undone and all nutrition data will be permanently removed.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-base font-medium border-r border-gray-200 dark:border-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteMeal}
                  className="px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-base font-medium flex items-center justify-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete Forever
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>,
    document.body
  );
};

export default NutritionAnalysisModal; 